<template>
  <div
    class="employee-column"
    :class="{ last: isLast }"
    :style="{
      display: 'grid',
      gridTemplateRows: `repeat(${timeMarks.length}, 32px)`,
      width: colWidth + 'px',
      minWidth: colWidth + 'px',
      maxWidth: colWidth + 'px'
    }"
  >
    <!-- Ячейки расписания: смена, перерыв, запись, вне смены -->
    <template v-for="(slot, idx) in slots" :key="'slot-'+idx">
      <!-- Перерыв -->
      <div
        v-if="slot.type === 'break'"
        class="cell break-cell"
        :class="slot.breakClass"
        :style="{ gridRow: `span ${slot.rowspan}` }"
      >
        <span class="break-icon" :class="slot.breakClass">{{ slot.icon }}</span>
        <span>{{ slot.label }}</span>
      </div>

      <!-- Запись с поповером -->
      <AppointmentPopover v-else-if="slot.type === 'appointment'" :appointment="slot.app">
        <div
          class="cell appointment-cell"
          :class="[slot.status, `status-${slot.app.status}`]"
          :style="{ gridRow: `span ${slot.rowspan}` }"
          @click="openViewModal(slot.app)">
          <div class="appointment-client">{{ slot.client_name || 'Клиент удалён' }}</div>
          <div v-if="slot.duration > 15" class="appointment-phone">
            {{ slot.client_phone || '—' }}
          </div>
          <div v-if="slot.duration > 30" class="appointment-services">{{ formatServices(slot.services) }}</div>
        </div>
      </AppointmentPopover>

      <!-- Смена (можно создать запись) -->
      <template v-else-if="slot.type === 'shift'">
        <div
          v-if="hasPermission('appointments.create')"
          class="cell shift-cell"
          :class="{ 'slot-past': isSlotPast(slot) }"
          @mouseenter="hoveredSlot = idx"
          @mouseleave="hoveredSlot = null"
          @click="openCreateModal(slot)"
        >
          <span class="cell-time">{{ slot.label }}</span>
          <span v-if="hoveredSlot === idx" class="add-appointment-tooltip">
            <span class="time-tooltip">{{ slot.label }}</span> Запись
          </span>
        </div>
        <div class="cell shift-cell disabled" v-else>
          <span class="cell-time">{{ slot.label }}</span>
        </div>
      </template>

      <!-- Вне смены -->
      <div v-else-if="slot.type === 'out'" class="cell out-of-shift"></div>
    </template>

    <!-- Модалка СОЗДАНИЯ -->
    <AppointmentCreateModal
      v-model:visible="showCreateModal"
      :date="currentDate"
      :time="modalStartTime"
      :employee="emp"
      @close="closeModals"
    />

    <!-- Модалка ПРОСМОТРА -->
    <AppointmentViewModal
      v-if="showViewModal"
      :date="currentDate"
      :appointment="modalAppointment"
      @close="closeModals"
      @edit="openEditModal"
      @open-complete="openCompleteModal"
    />

    <!-- Модалка Выполнить -->
    <AppointmentCompleteModal
      v-if="showCompleteModal"
      :appointment="modalAppointment"
      :date="currentDate"
      @close="showCompleteModal = false"
    />

    <!-- Модалка РЕДАКТИРОВАНИЯ -->
    <AppointmentEditModal
      v-if="showEditModal"
      v-model:visible="showCreateModal"
      :date="currentDate"
      :appointment="modalAppointment"
      @close="closeModals"
    />
  </div>
</template>

<script>
import AppointmentPopover from './AppointmentPopover.vue'
import AppointmentViewModal from './AppointmentViewModal.vue'
import AppointmentEditModal from './AppointmentEditModal.vue'
import AppointmentCreateModal from './AppointmentCreateModal.vue'
import AppointmentCompleteModal from '@/components/util/calendar/AppointmentCompleteModal.vue'
import { mapState } from 'vuex'
import { hasPermission } from '@/components/util/accessHelper'

export default {
  name: 'ScheduleEmployeeColumn',
  components: {
    AppointmentCompleteModal,
    AppointmentPopover,
    AppointmentViewModal,
    AppointmentEditModal,
    AppointmentCreateModal
  },
  props: {
    emp: { type: Object, required: true },
    timeMarks: { type: Array, required: true },
    isLast: { type: Boolean, default: false },
    colWidth: { type: Number, default: 200 },
    appointments: { type: Array, default: () => [] },
    currentDate: { type: String, required: true }
  },
  data() {
    return {
      showCompleteModal: false,
      hoveredSlot: null,
      showCreateModal: false,
      showViewModal: false,
      showEditModal: false,
      modalStartTime: '',
      modalAppointment: null
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    nowMin() {
      if (this.currentDate !== this.today) return null
      const now = new Date()
      return now.getHours() * 60 + now.getMinutes()
    },
    today() {
      return new Date().toISOString().slice(0, 10)
    },
    myAppointments() {
      return this.appointments
        .filter(
          app =>
            app.user_id === this.emp.user_id &&
            app.room_id === this.emp.room_id
        )
        .sort((a, b) => this.timeToMin(a.start_time) - this.timeToMin(b.start_time))
    },
    slots() {
      const slots = []
      const shiftStart = this.timeToMin(this.emp.start_time)
      const shiftEnd = this.timeToMin(this.emp.end_time)
      const appointments = this.myAppointments
      let i = 0
      while (i < this.timeMarks.length) {
        const t = this.timeMarks[i]
        // Вне смены
        if (t < shiftStart || t >= shiftEnd) {
          slots.push({ type: 'out' })
          i++
          continue
        }
        // Перерыв
        const br = (this.emp.breaks || []).find(b => this.timeToMin(b.start) === t)
        if (br) {
          const startIdx = i
          const endIdx = this.timeMarks.findIndex(tm => tm === this.timeToMin(br.end))
          const rowspan = endIdx > startIdx ? endIdx - startIdx : 1
          // Сюда прокидываем иконку и класс
          let icon = '⏸️', breakClass = 'break'
          if (br.type === 'lunch') {
            icon = '🍽'
            breakClass = 'lunch'
          } else if (br.type === 'maintenance') {
            icon = '🛠'
            breakClass = 'maintenance'
          }
          slots.push({
            type: 'break',
            rowspan,
            label: this.breakLabel(br),
            icon,
            breakClass
          })
          i += rowspan
          continue
        }
        // Запись
        const appIdx = appointments.findIndex(a => this.timeToMin(a.start_time) === t)
        if (appIdx !== -1) {
          const app = appointments[appIdx]
          let duration = app.duration_time
          if (!duration) {
            duration = (app.services || []).reduce((sum, s) => sum + (s.duration || 0), 0)
          }
          duration = Math.ceil(duration / 15) * 15
          const startIdx = i
          let endIdx = startIdx + Math.round(duration / 15)
          const nextApp = appointments.find(a => this.timeToMin(a.start_time) > t)
          if (nextApp && this.timeToMin(nextApp.start_time) < this.timeMarks[endIdx]) {
            endIdx = this.timeMarks.findIndex(tm => tm === this.timeToMin(nextApp.start_time))
          }
          const rowspan = endIdx > startIdx ? endIdx - startIdx : 1
          const calc_end = this.formatTime(this.timeMarks[startIdx] + duration)
          slots.push({
            type: 'appointment',
            rowspan,
            client_name: app.client_name,
            client_phone: app.client_phone,
            services: app.services,
            duration,
            status: this.appointmentStatus(app),
            start_time: app.start_time,
            calc_end,
            app, // весь объект для передачи в поповер/модалку
            id: app.id
          })
          i += rowspan
          continue
        }
        // Обычная смена
        slots.push({ type: 'shift', label: this.formatTime(t) })
        i++
      }
      return slots
    }
  },
  methods: {
    hasPermission,
    isSlotPast(slot) {
      try {
        if (!slot || typeof slot.label !== 'string' || !slot.label.includes(':')) return false

        // Берём таймзону пользователя или ставим Moscow по умолчанию
        const tz = (this.user && this.user.timezone) ? this.user.timezone : 'Europe/Moscow'

        // Текущее время в этой таймзоне
        const nowTz = new Date(new Date().toLocaleString('en-US', { timeZone: tz }))
        const yyyy = nowTz.getFullYear()
        const mm = String(nowTz.getMonth() + 1).padStart(2, '0')
        const dd = String(nowTz.getDate()).padStart(2, '0')
        const todayTz = `${yyyy}-${mm}-${dd}`
        const currentMinutes = nowTz.getHours() * 60 + nowTz.getMinutes()

        // Проверка даты
        if (this.currentDate < todayTz) {
          return true
        }

        if (this.currentDate === todayTz) {
          const [h, m] = slot.label.split(':').map(Number)
          const slotMinutes = h * 60 + m
          return slotMinutes < currentMinutes
        }

        // Будущие даты разрешены
        return false
      } catch (e) {
        return false
      }
    },
    timeToMin(str) {
      if (!str) return 0
      const [h, m] = str.split(':').map(Number)
      return h * 60 + m
    },
    breakLabel(br) {
      switch (br.type) {
        case 'lunch':
          return 'Обед'
        case 'maintenance':
          return 'Профилактика'
        case 'break':
          return 'Перерыв'
        default:
          return 'Перерыв'
      }
    },
    formatTime(t) {
      const h = Math.floor(t / 60), m = t % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    },
    formatServices(services) {
      if (!Array.isArray(services) || !services.length) return ''
      let txt = services.map(s => s.name).join(', ')
      return txt.length > 22 ? txt.slice(0, 20) + '…' : txt
    },
    appointmentStatus(app) {
      // CSS-класс: past/current/future по времени
      const now = new Date()
      const today = now.toISOString().slice(0, 10)
      const start = this.timeToMin(app.start_time)
      const end = this.timeToMin(app.end_time || '') || start // fallback
      const h = now.getHours(), m = now.getMinutes()
      const curr = h * 60 + m
      if (app.date < today || (app.date === today && end < curr)) return 'past'
      if (app.date === today && start <= curr && curr < end) return 'current'
      return 'future'
    },
    openCreateModal(slot) {
      if (this.isSlotPast(slot)) return
      this.modalStartTime = slot.label
      this.showCreateModal = true
    },
    openViewModal(app) {
      this.modalAppointment = app
      this.showViewModal = true
    },
    openEditModal() {
      this.showViewModal = false
      this.showEditModal = true
    },
    closeModals() {
      this.showCreateModal = false
      this.showViewModal = false
      this.showEditModal = false
      this.modalStartTime = ''
    },
    openCompleteModal() {
      // Открываем модалку "Выполнить" с актуальным appointment
      this.showViewModal = false
      this.showCompleteModal = true
    }
  }
}
</script>

<style scoped>
.employee-column {
  box-sizing: border-box;
  position: relative;
}

.employee-column.last {
  border-right: none !important;
}

.cell {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent !important;
  box-sizing: border-box;
  position: relative;
}

.employee-column > .cell:first-child {
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  overflow: hidden;
}

.break-cell {
  background: var(--cal-bg-break) !important;
  border-left: 3px solid var(--cal-warning);
  color: var(--cal-text-primary);
  font-weight: 500;
}

.shift-cell .cell-time {
  display: none;
}

.out-of-shift {
  background: var(--cal-bg-out);
  border-color: transparent;
}

.appointment-cell {
  background: var(--cal-bg-appointment) !important;
  color: var(--cal-text-inverse);
  border-radius: 0;
  font-size: 14px;
  padding: 8px 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 30px;
  box-shadow: none;
  pointer-events: auto;
  cursor: pointer;
  border: none;
  border-top: 1px solid rgba(137, 173, 227, 0.28);
  border-bottom: 1px solid rgba(137, 173, 227, 0.2);
  transition: background 0.2s, filter 0.2s;
  margin: 0;
  font-weight: 600;
  text-align: center;
  box-shadow: inset 0 0 0 1px rgba(122, 164, 233, 0.1);
}
.popover-client.deleted {
  color: var(--cal-danger);
}
.appointment-cell.past {
  background: var(--cal-bg-appointment-past) !important;
  color: var(--cal-text-muted);
}

.appointment-cell.current {
  background: var(--cal-bg-appointment-current) !important;
  color: var(--cal-text-inverse);
  font-weight: 600;
}

.appointment-client {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%; /* или укажи px, например 160px */
}

.appointment-phone {
  font-size: 13px;
  line-height: 1.3;
  color: var(--cal-accent);
  font-weight: 700;
}

.appointment-services {
  font-size: 13px;
  color: #8fa7d2;
}

.add-appointment-tooltip {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: var(--cal-bg-hover);
  color: var(--cal-text-inverse);
  padding: 2px 10px;
  font-size: 12px;
  display: flex;
  white-space: nowrap;
  pointer-events: none;
  z-index: 20;
  box-shadow: none;
  align-items: center;
  justify-content: center;

  .time-tooltip {
    position: absolute;
    color: var(--cal-text-muted);
    left: 12px;
  }
}

.shift-cell {
  background: var(--cal-bg-cell-alt) !important;
  position: relative;
  cursor: pointer;
}

.employee-column .shift-cell:nth-child(even) {
  background: var(--cal-bg-cell) !important;
}

.shift-cell.disabled {
  background: var(--cal-bg-cell-alt) !important;
  cursor: default;
  pointer-events: none;
  color: var(--cal-text-dim);
}

.break-cell.lunch {
  background: var(--cal-bg-break) !important;
  border-left: 3px solid var(--cal-warning);
  color: var(--cal-text-primary);
}

.break-cell.break {
  background: var(--cal-bg-break) !important;
  border-left: 3px solid var(--cal-accent-strong);
  color: var(--cal-text-primary);
}

.break-cell.maintenance {
  background: var(--cal-bg-break) !important;
  border-left: 3px solid var(--cal-text-muted);
  color: var(--cal-text-regular);
}

.break-icon {
  font-size: 18px;
  margin-right: 6px;
  vertical-align: middle;
}

.slot-past {
  background: var(--cal-bg-cell-alt) !important;
  color: var(--cal-text-dim) !important;
  cursor: not-allowed !important;
  pointer-events: none;
  opacity: 0.6;
}

.appointment-cell.status-planned {
  border-left: 4px solid var(--cal-warning);
}
.appointment-cell.status-confirmed {
  border-left: 4px solid var(--cal-accent-strong);
}
.appointment-cell.status-done {
  border-left: 4px solid var(--cal-success);
}
.appointment-cell.status-no_show {
  border-left: 4px solid var(--cal-danger);
  background: var(--cal-bg-appointment-past) !important;
}

.appointment-cell:hover {
  filter: brightness(1.05);
}
</style>
